<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Pick your texting number</h1>
    <p class="a2p-sub">
      Your registration is approved — now you need an SMS-capable phone number to
      send messages. Search by area code to find local numbers, or leave it blank
      to see what is available.
    </p>

    <!-- Numbers you already have -->
    <div v-if="existingNumbers && existingNumbers.length > 0" class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Already yours</div>
        <h2>Your current phone numbers</h2>
      </div>
      <div class="a2p-phone-list">
        <div v-for="n in existingNumbers" :key="n.sid" class="a2p-phone-row existing">
          <div class="number">{{ formatPhoneNumber(n.phoneNumber) }}</div>
          <div class="meta">
            <span>📅 {{ formatDate(n.dateCreated) }}</span>
            <span v-if="n.locality">📍 {{ n.locality }}, {{ n.region }}</span>
            <span class="badge ok">SMS ready</span>
          </div>
          <div class="check">✓</div>
        </div>
      </div>
    </div>

    <!-- Search for new numbers -->
    <div class="a2p-section">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Purchase</div>
        <h2>{{ existingNumbers && existingNumbers.length > 0 ? 'Add more numbers' : 'Your first phone number' }}</h2>
        <p class="a2p-section-desc">
          Numbers are purchased under your dedicated Twilio subaccount.
        </p>
      </div>

      <!-- The search box and the list below are SHARED with the WhatsApp wizard's number step
           (components/phone-numbers, 2026-09-11) — one picker, two screens. Each screen still owns
           its own search request and its own purchase: this one buys through A2P, which attaches
           the number to the A2P messaging setup; WhatsApp must not. -->
      <div class="a2p-phone-search">
        <AreaCodeSearch
          label="Search by area code (optional)"
          hint="Leave it blank to see what is available."
          placeholder="e.g. 555"
          allow-empty
          :searching="isSearchingNumbers"
          :disabled="isPurchasing"
          @search="doSearch"
        />
      </div>

      <!-- Results.
           ONE number per practice, by design — SMS capability is derived from the
           single GENERAL_PHONE setting, and the backend refuses a second purchase
           for a site that already has one. This used to be a multi-select, which
           offered something the server would not honour.

           NO PRICE (Saumil, 2026-09-11). Teamio includes the number — the practice is
           never charged Twilio's monthly fee — so no figure is shown next to a number,
           on the button, or in the confirmation. -->
      <div v-if="availableNumbers && availableNumbers.length > 0" class="a2p-phone-results">
        <div class="a2p-section-eyebrow" style="margin-bottom: 10px">Available numbers</div>
        <PhoneNumberList
          v-model="selectedNumber"
          :numbers="availableNumbers"
          :disabled="isPurchasing"
          label="Available numbers"
        />

        <A2PCallout v-if="selectedNumber" type="success">
          <strong>{{ formatPhoneNumber(selectedNumber) }} selected.</strong>
          <div class="cost-note">
            Practice numbers are not released once they are in use, so pick one you are
            happy to keep.
          </div>
        </A2PCallout>
      </div>

      <A2PCallout v-else-if="searchPerformed && !isSearchingNumbers" type="warn">
        No numbers available for that search. Try a different area code, or leave it
        blank to see what is available.
      </A2PCallout>
    </div>

    <div class="a2p-actions">
      <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')">
        Back
      </button>
      <div style="display: flex; gap: 10px">
        <button
          v-if="existingNumbers && existingNumbers.length > 0"
          type="button"
          class="a2p-btn a2p-btn-secondary"
          @click="$emit('next')"
        >
          Skip & continue →
        </button>
        <button
          type="button"
          class="a2p-btn a2p-btn-primary"
          @click="purchaseSelectedNumber"
          :disabled="!selectedNumber || isPurchasing"
        >
          <b-spinner v-if="isPurchasing" small class="mr-2"></b-spinner>
          {{ isPurchasing ? 'Getting your number…' : 'Get this number' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import mixin from '@/mixin';
import A2PCallout from './components/A2PCallout.vue';
import AreaCodeSearch from '../components/phone-numbers/AreaCodeSearch.vue';
import PhoneNumberList from '../components/phone-numbers/PhoneNumberList.vue';
import { formatUsNumber } from '../components/phone-numbers/phoneNumbers';

export default {
  name: 'A2PWizardPhoneNumbers',
  components: { A2PCallout, AreaCodeSearch, PhoneNumberList },
  mixins: [mixin],
  props: {
    eyebrow: { type: String, default: '' },
    existingNumbers: { type: Array, default: () => [] },
  },
  data() {
    return {
      availableNumbers: [],
      // ONE number, not a list — see the template comment on the results block.
      selectedNumber: '',
      isSearchingNumbers: false,
      searchPerformed: false,
      isPurchasing: false,
    };
  },
  methods: {
    ...mapActions('a2p', ['searchAvailableNumbers', 'purchasePhoneNumber']),

    // `areaCode` arrives from the shared AreaCodeSearch, already validated ('' = blank search).
    async doSearch(areaCode = '') {
      try {
        this.isSearchingNumbers = true;
        this.searchPerformed = true;
        const payload = { siteID: this.siteID, areaCode: areaCode || undefined };
        const response = await this.$store.dispatch('a2p/searchAvailableNumbers', payload);
        // The response still carries Twilio's price; it is deliberately NOT read here (2026-09-11 —
        // Teamio includes the number, so no price is shown to the practice).
        this.availableNumbers = response.data || [];
        // A stale selection from a previous search must not survive into a new
        // result set — it would be purchasable while no longer visible.
        this.selectedNumber = '';
      } catch (error) {
        console.error('Error searching numbers:', error);
        const msg = error.response?.data?.message || error.message || '';
        if (msg.includes('Twilio credentials not configured')) {
          this.$toast.warning('A2P registration must be completed before searching for phone numbers.');
        } else {
          this.$toast.error('Failed to search for available numbers. Please try again.');
        }
        this.availableNumbers = [];
      } finally {
        this.isSearchingNumbers = false;
      }
    },

    async purchaseSelectedNumber() {
      if (!this.selectedNumber) {
        this.$toast.warning('Please pick a number first');
        return;
      }

      // Confirmed before anything is bought — not for money any more (Teamio includes the number,
      // 2026-09-11) but because it is ONE-WAY: a number is not released once patients use it.
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Get ${this.formatPhoneNumber(this.selectedNumber)} for your practice? `
        + 'A number cannot be released once it is in use, so pick one you are happy to keep.',
        {
          title: 'Confirm your number',
          okVariant: 'primary',
          okTitle: 'Yes, get this number',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );
      if (!confirmed) return;

      try {
        this.isPurchasing = true;
        await this.$store.dispatch('a2p/purchasePhoneNumber', {
          siteID: this.siteID,
          phoneNumber: this.selectedNumber,
        });
        this.$toast.success(`${this.formatPhoneNumber(this.selectedNumber)} is now your practice's texting number.`);
        this.$emit('next', {
          purchasedNumbers: true,
          phoneNumbers: [this.selectedNumber],
        });
      } catch (error) {
        console.error('Error purchasing number:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to purchase the number.');
      } finally {
        this.isPurchasing = false;
      }
    },

    // The shared formatter — the same one the number list renders with.
    formatPhoneNumber(n) {
      return n ? formatUsNumber(n) : '';
    },
    formatDate(d) {
      return d ? moment(d).format('MMM D, YYYY') : '';
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
/* The box around the shared AreaCodeSearch; its own label/input/button styles live in that
   component (components/phone-numbers, 2026-09-11). */
.a2p-phone-search {
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
  padding: 18px 20px;
  margin: 12px 0 20px;
}

.a2p-phone-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.a2p-phone-row {
  display: grid;
  grid-template-columns: auto 1fr 28px;
  gap: 16px;
  align-items: center;
  padding: 14px 18px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-s);
  transition: border-color 0.15s, background 0.15s;
}
/* Only the "Already yours" rows use these now; the pickable rows are the shared PhoneNumberList. */
.a2p-phone-row.existing {
  border-color: var(--a2p-success);
  background: var(--a2p-success-soft);
}
.a2p-phone-row .number {
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--a2p-fg);
  font-size: 15px;
}
.a2p-phone-row .meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 12px;
  color: var(--a2p-fg-3);
}
.a2p-phone-row .meta .badge {
  padding: 2px 8px;
  border-radius: 99px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.a2p-phone-row .meta .badge.ok   { background: var(--a2p-success-soft); color: var(--a2p-success-ink); }
.a2p-phone-row .meta .badge.info { background: var(--a2p-accent-soft);  color: var(--a2p-accent-ink); }
/* The `.badge.price` rule was REMOVED 2026-09-11 with the price it styled — Teamio includes the number. */
.cost-note {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 400;
  opacity: 0.85;
}
.a2p-phone-row .check {
  font-size: 18px;
  font-weight: 700;
  color: var(--a2p-fg-3);
  justify-self: center;
}
.a2p-phone-row.existing .check {
  color: var(--a2p-success-ink);
}

.a2p-phone-results { margin-top: 16px; }
</style>
